import { blackScholesCall } from "./blackScholes";
import {
  getTradingDaysUntilExpiration,
  getDaysToExpiration,
  daysToYears,
  getYearsToExpirationFromNow,
} from "./tradingDays";

/**
 * Generate scenarios (1% to 25% price movements)
 * @param {number} stockPrice - Current stock price
 * @param {number} strike - Strike price
 * @param {number} timeToExpiration - Time to expiration in years (for future price calc)
 * @param {number} iv - Implied volatility (as decimal)
 * @param {number} riskFreeRate - Risk-free rate (as decimal)
 * @param {number} currentOptionPrice - Current option theoretical price
 * @returns {object} - Scenarios object
 */
function generateScenarios(
  stockPrice,
  strike,
  timeToExpiration,
  iv,
  riskFreeRate,
  currentOptionPrice,
) {
  const scenarios = {};

  // Generate scenarios from 1% to 25%
  for (let movePercent = 1; movePercent <= 25; movePercent++) {
    const futureStockPrice = stockPrice * (1 + movePercent / 100);

    const theoreticalPriceFuture = blackScholesCall(
      futureStockPrice,
      strike,
      timeToExpiration,
      riskFreeRate,
      iv,
    );

    const priceChange = theoreticalPriceFuture - currentOptionPrice;
    const priceChangePct =
      currentOptionPrice > 0 ? (priceChange / currentOptionPrice) * 100 : 0;

    scenarios[`${movePercent}%`] = {
      future_stock_price: Math.round(futureStockPrice * 100) / 100,
      theoretical_price_future:
        Math.round(theoreticalPriceFuture * 10000) / 10000,
      price_change: Math.round(priceChange * 10000) / 10000,
      price_change_pct: Math.round(priceChangePct * 100) / 100,
    };
  }

  return scenarios;
}

/**
 * Generate trading days data for a strike
 * @param {number} stockPrice - Current stock price
 * @param {number} strike - Strike price
 * @param {dayjs.Dayjs} expirationDate - Expiration date
 * @param {number} iv - Implied volatility (used for both entry price and future scenarios)
 * @param {number} riskFreeRate - Risk-free rate
 * @param {dayjs.Dayjs} customDate - Actual "now" instant (optional, defaults to current time)
 * @returns {array} - Trading days array
 */
export function generateTradingDaysForStrike(
  stockPrice,
  strike,
  expirationDate,
  iv,
  riskFreeRate,
  customDate = null,
) {
  const tradingDays = getTradingDaysUntilExpiration(expirationDate, customDate);
  const tradingDaysData = [];

  // Entry / "now" price uses the real clock time (converted to ET internally),
  // not the 16:00 ET session close.
  const theoreticalPriceNow = blackScholesCall(
    stockPrice,
    strike,
    getYearsToExpirationFromNow(customDate, expirationDate),
    riskFreeRate,
    iv,
  );

  tradingDays.forEach((tradingDay, index) => {
    // Future marks are always the 16:00 ET close of that trading day.
    const daysToExpiration = getDaysToExpiration(tradingDay, expirationDate);

    // Time to expiration for this trading day. On the expiration day itself
    // this is exactly 0 — blackScholesCall already prices that as pure
    // intrinsic value, so it must not be floored to a fake extra day of
    // time value.
    const timeToExpiration = daysToYears(daysToExpiration);

    // Sequential count of trading sessions held (1 = first trading day after
    // purchase), not calendar days — so "hold for N days" always lands on a
    // real trading day regardless of weekends/holidays in between.
    const tradingDaysPassed = index + 1;

    // Generate scenarios for this trading day
    const scenarios = generateScenarios(
      stockPrice,
      strike,
      timeToExpiration,
      iv,
      riskFreeRate,
      theoreticalPriceNow,
    );

    tradingDaysData.push({
      trading_day: tradingDay.format("YYYY-MM-DD"),
      day_of_week: tradingDay.format("dddd"),
      trading_days_passed: tradingDaysPassed,
      days_to_expiration: daysToExpiration,
      current_stock_price: Math.round(stockPrice * 100) / 100,
      theoretical_price_now: Math.round(theoreticalPriceNow * 10000) / 10000,
      scenarios,
    });
  });

  return tradingDaysData;
}
